import React, { useRef, useEffect, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Button } from '@/components/ui/button';
import { Camera, CameraOff, RotateCcw, Flashlight, FlashlightOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
interface QrScannerProps {
  onResult: (text: string) => void;
  onError?: (error: string) => void;
  className?: string;
}
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
}
export const QrScanner: React.FC<QrScannerProps> = ({
  onResult,
  onError,
  className
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReader = useRef<BrowserMultiFormatReader | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanningRef = useRef<boolean>(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);
  const [lastScanTime, setLastScanTime] = useState<number>(0);
  const stopScanning = useCallback(() => {
    scanningRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
    setError(null);
  }, []);
  const startScanning = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      setError(null);
      setIsScanning(true);
      scanningRef.current = true;

      // Demander la permission et obtenir le flux vidéo
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: 'environment',
          // Caméra arrière par défaut
          width: {
            ideal: 1280
          },
          height: {
            ideal: 720
          }
        },
        audio: false
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setHasPermission(true);

      // Afficher le flux vidéo
      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      // Vérifier le support du flash
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities;
      if (capabilities.torch) {
        setTorchSupported(true);
      }

      // Initialiser le reader ZXing
      if (!codeReader.current) {
        codeReader.current = new BrowserMultiFormatReader();
      }

      // Scan continu manual avec requestAnimationFrame
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const scan = async () => {
        if (!scanningRef.current || !videoRef.current || !codeReader.current || !context) return;
        try {
          // Capturer une frame du vidéo sur le canvas
          canvas.width = videoRef.current.videoWidth;
          canvas.height = videoRef.current.videoHeight;
          context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

          // Décoder l'image du canvas
          const dataURL = canvas.toDataURL('image/png');
          const result = await codeReader.current.decodeFromImageUrl(dataURL);
          if (result && scanningRef.current) {
            const scannedCode = result.getText();
            const currentTime = Date.now();

            // Vérifier si c'est le même code et si moins de 2 secondes se sont écoulées
            if (lastScannedCode === scannedCode && currentTime - lastScanTime < 2000) {
              // Ignorer le scan, trop tôt pour rescanner le même produit
              return;
            }

            // Mettre à jour les infos du dernier scan
            setLastScannedCode(scannedCode);
            setLastScanTime(currentTime);
            toast.success('QR Code détecté !');
            onResult(scannedCode);
          }
        } catch (error) {
          // Continue scanning on decode errors
        }
        if (scanningRef.current) {
          setTimeout(() => requestAnimationFrame(scan), 100); // Limiter à ~10 FPS
        }
      };
      scan();
    } catch (err: any) {
      console.error('Erreur lors du démarrage du scanner:', err);
      let errorMessage = 'Erreur inconnue';
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permission caméra refusée. Veuillez autoriser l\'accès à la caméra.';
        setHasPermission(false);
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Aucune caméra trouvée sur cet appareil.';
      } else if (err.name === 'NotSupportedError') {
        errorMessage = 'Caméra non supportée par ce navigateur.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Caméra déjà utilisée par une autre application.';
      } else {
        errorMessage = err.message || 'Impossible d\'accéder à la caméra.';
      }
      setError(errorMessage);
      setIsScanning(false);
      scanningRef.current = false;
      onError?.(errorMessage);
      toast.error(errorMessage);
    }
  }, [onResult, onError]);
  const switchCamera = useCallback(async () => {
    if (isScanning) {
      stopScanning();
      setTimeout(() => {
        startScanning();
      }, 500);
    }
  }, [isScanning, stopScanning, startScanning]);
  const toggleTorch = useCallback(async () => {
    if (!streamRef.current || !torchSupported) return;
    try {
      const track = streamRef.current.getVideoTracks()[0];
      const newTorchState = !torchEnabled;

      // Utiliser any pour éviter les problèmes de type avec torch
      await track.applyConstraints({
        advanced: [{
          torch: newTorchState
        } as any]
      });
      setTorchEnabled(newTorchState);
      toast.success(newTorchState ? 'Flash activé' : 'Flash désactivé');
    } catch (error) {
      console.error('Erreur lors du basculement du flash:', error);
      toast.error('Impossible de contrôler le flash');
    }
  }, [torchEnabled, torchSupported]);
  useEffect(() => {
    // Cleanup au démontage
    return () => {
      stopScanning();
    };
  }, [stopScanning]);
  return <div className={cn("w-full", className)}>
      {/* Zone vidéo avec cadre de guidage */}
      <div className={`relative bg-muted rounded-lg overflow-hidden aspect-video w-full sm:aspect-square sm:max-w-sm sm:mx-auto ${!isScanning ? 'hidden' : ''}`}>
        <video
          ref={videoRef}
          className={`w-full h-full object-cover ${!isScanning || !streamRef.current ? 'hidden' : ''}`}
          playsInline
          muted
        />
        
        {/* Cadre de guidage pour QR code */}
        {isScanning && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              {/* Zone de scan centrale - responsive */}
              <div className="w-40 h-40 sm:w-48 sm:h-48 border-2 border-primary bg-transparent relative">
                {/* Coins du cadre */}
                <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-md"></div>
                <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-md"></div>
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-md"></div>
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-md"></div>
                
                {/* Ligne de scan animée */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary animate-pulse"></div>
                </div>
              </div>
              
              {/* Instructions */}
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-center">
                <p className="text-sm text-primary font-medium whitespace-nowrap">
                  Placez le QR code dans le cadre
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Message d'erreur overlay */}
        {error && (
          <div className="absolute inset-0 bg-background/90 flex items-center justify-center p-2 sm:p-4">
            <div className="text-center">
              <CameraOff className="w-8 h-8 sm:w-12 sm:h-12 text-destructive mx-auto mb-2" />
              <p className="text-xs sm:text-sm text-destructive font-medium px-2">{error}</p>
            </div>
          </div>
        )}

        {/* Message de chargement */}
        {isScanning && !error && !streamRef.current && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center p-2 sm:p-4">
            <div className="text-center">
              <Camera className="w-8 h-8 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-xs sm:text-sm text-muted-foreground">Initialisation de la caméra...</p>
            </div>
          </div>
        )}
      </div>

      {/* Contrôles */}
      <div className="flex flex-wrap items-center justify-center gap-2 mt-3 sm:mt-4">
        {!isScanning ? <Button onClick={startScanning} className="gap-2 text-sm h-9 sm:h-10 px-4 sm:px-6">
            <Camera className="w-4 h-4" />
            <span className="hidden sm:inline">Démarrer scanner</span>
            <span className="sm:hidden">Scanner</span>
          </Button> : <>
            <Button onClick={stopScanning} variant="destructive" className="gap-2 text-sm h-9 sm:h-10 px-3 sm:px-4">
              <CameraOff className="w-4 h-4" />
              <span className="hidden sm:inline">Arrêter scanner</span>
              <span className="sm:hidden">Arrêter</span>
            </Button>
            
            {torchSupported && <Button onClick={toggleTorch} variant={torchEnabled ? "default" : "outline"} className="h-9 w-9 sm:h-10 sm:w-10">
                {torchEnabled ? <FlashlightOff className="w-4 h-4" /> : <Flashlight className="w-4 h-4" />}
              </Button>}
          </>}
      </div>

      {/* Informations sur les permissions */}
      {hasPermission === false && <div className="text-center p-3 sm:p-4 bg-destructive/10 border border-destructive/20 rounded-lg mt-4">
          <p className="text-sm text-destructive font-medium mb-2">Permission caméra refusée</p>
          <p className="text-xs text-muted-foreground">
            Veuillez autoriser l'accès à la caméra dans les paramètres de votre navigateur pour utiliser le scanner QR.
          </p>
        </div>}

      {/* Instructions d'utilisation */}
      <div className="text-center mt-3">
        <p className="text-xs text-muted-foreground px-4">
          Le scanner peut lire les QR codes dans toutes les orientations. 
          Assurez-vous d'avoir un bon éclairage pour de meilleurs résultats.
        </p>
      </div>
    </div>;
};